import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, ChevronLeft, ChevronRight, EyeOff, MessageCircle } from 'lucide-react';
import { fetchPublicSiteConfig, type SiteLive2DConfig, type SiteLive2DModelItem } from '@/api/site-config.api';

declare global {
  interface Window {
    L2Dwidget?: {
      init: (options: Record<string, unknown>) => void;
    };
  }
}

const STORAGE_KEY = 'yuguige_live2d_enabled';
const MODEL_STORAGE_KEY = 'yuguige_live2d_model_id';

type Live2DModelChangeEvent = CustomEvent<{ id?: string }>;

function removeLive2DElements() {
  const ids = ['live2d-widget', 'live2dcanvas'];
  ids.forEach((id) => {
    const node = document.getElementById(id);
    if (node && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  });
}

function getInitialEnabled() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw == null) return true;
  return raw === 'true';
}

function normalizeModelJsonPath(rawPath: string) {
  const raw = String(rawPath || '').trim();
  if (!raw) return raw;
  if (/^https?:\/\/unpkg\.com\//i.test(raw)) {
    return raw.replace(/^https?:\/\/unpkg\.com\//i, 'https://cdn.jsdelivr.net/npm/');
  }
  return raw;
}

async function ensureLive2DScriptLoaded() {
  if (window.L2Dwidget) return;

  await new Promise<void>((resolve) => {
    const existing = document.querySelector('script[data-live2d-script="1"]') as HTMLScriptElement | null;
    if (existing) {
      if (window.L2Dwidget) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/live2d-widget@3.1.4/lib/L2Dwidget.min.js';
    script.async = true;
    script.dataset.live2dScript = '1';
    script.onload = () => resolve();
    document.body.appendChild(script);
  });
}

function resolveActiveModel(config: SiteLive2DConfig | null, selectedId: string): SiteLive2DModelItem | null {
  const models = config?.models || [];
  if (!models.length) return null;
  return models.find((item) => item.id === selectedId) || models[0];
}

function getRandomText(texts: string[]) {
  if (!texts.length) return '';
  const index = Math.floor(Math.random() * texts.length);
  return texts[index] || '';
}

const Live2DWidget: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean>(getInitialEnabled);
  const [config, setConfig] = useState<SiteLive2DConfig | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [bubbleText, setBubbleText] = useState('');
  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [controlsStyle, setControlsStyle] = useState<React.CSSProperties>({ right: '184px', bottom: '112px' });
  const bubbleTimerRef = useRef<number | null>(null);
  const interactionCleanupRef = useRef<(() => void) | null>(null);

  const activeModel = useMemo(() => resolveActiveModel(config, selectedModelId), [config, selectedModelId]);

  const showBubble = useCallback((texts: string[]) => {
    const next = getRandomText(texts);
    if (!next) return;
    setBubbleText(next);
    setBubbleVisible(true);
    if (bubbleTimerRef.current) {
      window.clearTimeout(bubbleTimerRef.current);
    }
    bubbleTimerRef.current = window.setTimeout(() => setBubbleVisible(false), 2400);
  }, []);

  const chooseModelId = useCallback((live2d: SiteLive2DConfig | null, preferConfig: boolean) => {
    const models = live2d?.models || [];
    if (!models.length) return '';

    const configModelId = String(live2d?.selectedModelId || '').trim();
    const localModelId = String(localStorage.getItem(MODEL_STORAGE_KEY) || '').trim();

    const hasConfigModel = !!configModelId && models.some((item) => item.id === configModelId);
    const hasLocalModel = !!localModelId && models.some((item) => item.id === localModelId);

    if (preferConfig && hasConfigModel) return configModelId;
    if (hasLocalModel) return localModelId;
    if (hasConfigModel) return configModelId;
    return models[0]?.id || '';
  }, []);

  const loadConfig = useCallback(async (preferConfigModel: boolean) => {
    const siteConfig = await fetchPublicSiteConfig();
    const live2d = siteConfig.live2d || null;
    const normalizedLive2d: SiteLive2DConfig | null = live2d
      ? {
          ...live2d,
          models: (live2d.models || []).map((item) => ({
            ...item,
            modelJsonPath: normalizeModelJsonPath(item.modelJsonPath),
          })),
        }
      : null;
    setConfig(normalizedLive2d);

    const nextModelId = chooseModelId(normalizedLive2d, preferConfigModel);
    setSelectedModelId(nextModelId);
    if (nextModelId) {
      localStorage.setItem(MODEL_STORAGE_KEY, nextModelId);
    }
  }, [chooseModelId]);

  useEffect(() => {
    loadConfig(true).catch(() => {
      setConfig(null);
    });

    const onToggle = () => {
      setEnabled(getInitialEnabled());
    };

    const onModelChange = (event: Event) => {
      const detail = (event as Live2DModelChangeEvent).detail;
      const id = String(detail?.id || '').trim();
      if (!id) return;
      localStorage.setItem(MODEL_STORAGE_KEY, id);
      setSelectedModelId(id);
    };

    const onReload = () => {
      loadConfig(true).catch(() => undefined);
    };

    window.addEventListener('live2d:toggle', onToggle as EventListener);
    window.addEventListener('live2d:model-change', onModelChange as EventListener);
    window.addEventListener('live2d:reload', onReload as EventListener);

    return () => {
      window.removeEventListener('live2d:toggle', onToggle as EventListener);
      window.removeEventListener('live2d:model-change', onModelChange as EventListener);
      window.removeEventListener('live2d:reload', onReload as EventListener);
    };
  }, [loadConfig]);

  useEffect(() => {
    if (!config) return;
    const models = config.models || [];
    if (!models.length) return;

    if (!models.some((item) => item.id === selectedModelId)) {
      const fallback = chooseModelId(config, true);
      if (!fallback) return;
      localStorage.setItem(MODEL_STORAGE_KEY, fallback);
      setSelectedModelId(fallback);
    }
  }, [chooseModelId, config, selectedModelId]);

  useEffect(() => {
    const canShow = enabled && config?.enabled !== false;
    if (!canShow || !activeModel?.modelJsonPath) {
      if (interactionCleanupRef.current) {
        interactionCleanupRef.current();
        interactionCleanupRef.current = null;
      }
      removeLive2DElements();
      return;
    }

    const interactionTexts = Array.isArray(config?.interactionTexts) ? config.interactionTexts : [];

    const initWidget = async () => {
      await ensureLive2DScriptLoaded();
      removeLive2DElements();

      window.L2Dwidget?.init({
        model: {
          jsonPath: normalizeModelJsonPath(activeModel.modelJsonPath),
        },
        display: {
          position: 'right',
          width: 152,
          height: 312,
          hOffset: 8,
          vOffset: -6,
        },
        mobile: {
          show: true,
          scale: 0.8,
        },
        react: {
          opacityDefault: 0.95,
          opacityOnHover: 0.25,
        },
        dialog: {
          enable: config?.interactive === true,
          script: {
            'tap body': interactionTexts.length > 0 ? interactionTexts : ['你好呀～'],
            'tap face': interactionTexts.length > 0 ? interactionTexts : ['摸摸头～'],
          },
        },
      });

      if (interactionCleanupRef.current) {
        interactionCleanupRef.current();
        interactionCleanupRef.current = null;
      }

      if (config?.interactive && interactionTexts.length > 0) {
        const onInteract = () => showBubble(interactionTexts);

        const bindTimer = window.setTimeout(() => {
          const canvas = document.getElementById('live2dcanvas');
          if (!canvas) return;
          canvas.addEventListener('click', onInteract);
          canvas.addEventListener('mouseenter', onInteract);
          canvas.addEventListener('touchstart', onInteract, { passive: true });
          interactionCleanupRef.current = () => {
            canvas.removeEventListener('click', onInteract);
            canvas.removeEventListener('mouseenter', onInteract);
            canvas.removeEventListener('touchstart', onInteract);
          };
        }, 240);

        interactionCleanupRef.current = () => {
          window.clearTimeout(bindTimer);
        };
      }
    };

    initWidget();

    return () => {
      if (interactionCleanupRef.current) {
        interactionCleanupRef.current();
        interactionCleanupRef.current = null;
      }
      if (bubbleTimerRef.current) {
        window.clearTimeout(bubbleTimerRef.current);
        bubbleTimerRef.current = null;
      }
      removeLive2DElements();
    };
  }, [activeModel?.id, activeModel?.modelJsonPath, config, enabled, showBubble]);

  useEffect(() => {
    if (!enabled || config?.enabled === false) return;

    const updateControlsPosition = () => {
      const canvas = document.getElementById('live2dcanvas');
      if (!canvas) {
        setControlsStyle({ right: '184px', bottom: '112px' });
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const nextTop = Math.max(76, rect.top + rect.height * 0.22);
      const nextRight = Math.max(168, window.innerWidth - rect.left + 10);
      setControlsStyle({ top: `${nextTop}px`, right: `${nextRight}px` });
    };

    const timer = window.setInterval(updateControlsPosition, 260);
    updateControlsPosition();
    window.addEventListener('resize', updateControlsPosition);
    window.addEventListener('scroll', updateControlsPosition, { passive: true });

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', updateControlsPosition);
      window.removeEventListener('scroll', updateControlsPosition);
    };
  }, [config?.enabled, enabled, activeModel?.id]);

  const canShowWidget = enabled && config?.enabled !== false && !!activeModel;
  const modelList = config?.models || [];
  const interactionTexts = config?.interactionTexts || [];

  const switchModel = (step: number) => {
    if (!modelList.length) return;
    const currentIndex = Math.max(0, modelList.findIndex((item) => item.id === selectedModelId));
    const nextIndex = (currentIndex + step + modelList.length) % modelList.length;
    const nextId = modelList[nextIndex]?.id;
    if (!nextId) return;
    localStorage.setItem(MODEL_STORAGE_KEY, nextId);
    setSelectedModelId(nextId);
  };

  const hideWidget = () => {
    localStorage.setItem(STORAGE_KEY, 'false');
    setEnabled(false);
    window.dispatchEvent(new Event('live2d:toggle'));
  };

  const triggerInteraction = () => {
    if (!config?.interactive) return;
    showBubble(interactionTexts);
  };

  return (
    <>
      {canShowWidget ? (
        <div className="fixed z-[74] flex flex-col gap-2" style={controlsStyle}>
          {modelList.length > 1 ? (
            <button
              type="button"
              onClick={() => switchModel(-1)}
              className="h-9 w-9 border text-[var(--text-primary)] transition-colors hover:text-[var(--accent-color)]"
              style={{
                background: 'var(--bg-card)',
                borderColor: 'var(--border-light)',
              }}
              title="上一个模型"
            >
              <ChevronLeft size={16} className="mx-auto" />
            </button>
          ) : null}

          <button
            type="button"
            onClick={triggerInteraction}
            disabled={!config?.interactive}
            className="h-9 w-9 border text-[var(--text-primary)] transition-colors hover:text-[var(--accent-color)] disabled:opacity-45"
            style={{
              background: 'var(--bg-card)',
              borderColor: 'var(--border-light)',
            }}
            title={config?.interactive ? '互动' : '互动已关闭'}
          >
            {config?.interactive ? <MessageCircle size={15} className="mx-auto" /> : <Bot size={15} className="mx-auto" />}
          </button>

          {modelList.length > 1 ? (
            <button
              type="button"
              onClick={() => switchModel(1)}
              className="h-9 w-9 border text-[var(--text-primary)] transition-colors hover:text-[var(--accent-color)]"
              style={{
                background: 'var(--bg-card)',
                borderColor: 'var(--border-light)',
              }}
              title="下一个模型"
            >
              <ChevronRight size={16} className="mx-auto" />
            </button>
          ) : null}

          <button
            type="button"
            onClick={hideWidget}
            className="h-9 w-9 border text-[var(--text-primary)] transition-colors hover:text-[var(--accent-color)]"
            style={{
              background: 'var(--bg-card)',
              borderColor: 'var(--border-light)',
            }}
            title="隐藏看板娘"
          >
            <EyeOff size={15} className="mx-auto" />
          </button>
        </div>
      ) : null}

      {bubbleVisible && bubbleText ? (
        <div className="fixed bottom-[248px] right-4 sm:right-8 z-[73] pointer-events-none max-w-[260px] border border-[var(--border-light)] bg-[var(--bg-card)] px-3 py-2 text-xs text-[var(--text-primary)] shadow-lg">
          {bubbleText}
        </div>
      ) : null}
    </>
  );
};

export default Live2DWidget;
